import { defineTable } from "convex/server";
import { v } from "convex/values";
import { internalControl } from "../../lib/shared/validators";
import { notificationPreferencesValidator } from "../notifications/preferences";
import { dashboardConfigValidator } from "../dashboard";

export const userRoles = v.union(
  v.literal("super_admin"),
  v.literal("company_admin"),
  v.literal("company_user"),
);

export const onboardingStepStatus = v.union(
  v.literal("pending"),
  v.literal("completed"),
  v.literal("skipped"),
);

export const onboardingProgress = v.object({
  completed: v.boolean(),
  currentStep: v.number(),
  steps: v.object({
    companySetup: onboardingStepStatus,
    businessType: onboardingStepStatus,
    currencyTimezone: onboardingStepStatus,
    moduleSelection: onboardingStepStatus,
  }),
  whatsapp: v.optional(v.string()),
  selectedModule: v.optional(v.string()),
  completedAt: v.optional(v.number()),
});

/**
 * Preferencias de idioma y región del usuario
 */
export const localePreferencesValidator = v.object({
  /** Idioma de la UI (es, en) */
  language: v.string(),
  /** Zona horaria (ej: America/Tegucigalpa) */
  timezone: v.optional(v.string()),
  /** Formato de fecha preferido */
  dateFormat: v.optional(v.string()),
  /** Primer día de la semana (0=domingo, 1=lunes) */
  firstDayOfWeek: v.optional(v.number()),
});

/**
 * Preferencias de UI para modales en web.
 * `auto` cambia entre Dialog y Sheet según el viewport.
 */
export const uiModalPreferencesValidator = v.object({
  /** Variante del modal: dialog (centrado) | sheet (lateral) | auto (responsive). */
  variant: v.union(
    v.literal("dialog"),
    v.literal("sheet"),
    v.literal("auto"),
  ),
  /** Lado del sheet cuando se fuerza sheet o cuando auto resuelve a sheet. */
  sheetSide: v.optional(
    v.union(v.literal("left"), v.literal("right"), v.literal("top"), v.literal("bottom")),
  ),
  /** Breakpoint en px para la resolución de variant="auto". Default 640 (sm Tailwind). */
  mobileBreakpointPx: v.optional(v.number()),
  /** Opacidad del overlay del sheet en porcentaje (0-100). Default 50. */
  sheetOverlayOpacity: v.optional(v.number()),
  /** Intensidad del backdrop-blur del sheet en px (0-20). Default 4. */
  sheetBackdropBlurPx: v.optional(v.number()),
  /**
   * Ancho en px del sheet lateral elegido arrastrando su borde, por formulario
   * (`default` para los que no tienen clave propia; `c_<id>` por colección).
   */
  sheetWidths: v.optional(v.record(v.string(), v.number())),
});

export const validatorUsers = v.object({
  // new fields (didn't exist in old version)
  name: v.optional(v.string()),
  image: v.optional(v.id("_storage")),
  email: v.string(),
  emailVerificationTime: v.optional(v.number()),
  phone: v.optional(v.string()),
  phoneVerificationTime: v.optional(v.number()),
  isAnonymous: v.optional(v.boolean()),
  isSuperAdmin: v.optional(v.boolean()),
  roleId: v.optional(v.id("roles")),
  branchIds: v.optional(v.array(v.id("branches"))),
  defaultBranchId: v.optional(v.id("branches")),
  activeBranchId: v.optional(v.id("branches")),
  dodoCustomerId: v.optional(v.string()),
  onboardingProgress: v.optional(onboardingProgress),
  notificationPreferences: v.optional(notificationPreferencesValidator),
  dashboardConfig: v.optional(dashboardConfigValidator),
  /** Config del dashboard mobile (independiente del web). */
  mobileDashboardConfig: v.optional(dashboardConfigValidator),
  /** Preferencias de idioma y región */
  localePreferences: v.optional(localePreferencesValidator),
  /** Preferencias de modales en la UI web (dialog vs sheet). */
  uiModalPreferences: v.optional(uiModalPreferencesValidator),
  /** Modelo LLM preferido por thread: { [threadId]: modelId } */
  aiThreadModels: v.optional(v.record(v.string(), v.string())),
  /** Modo del asistente por thread: { [threadId]: "auto"|"manual"|"accept"|"plan" } */
  aiThreadModes: v.optional(v.record(v.string(), v.string())),
  debugMode: v.optional(v.boolean()),
  internalControl: v.optional(internalControl),
});

export const users = defineTable(validatorUsers)
  // `@convex-dev/auth` busca el índice llamado EXACTAMENTE `email` en la tabla
  // users (uniqueUserWithVerifiedEmail al deduplicar por email verificado en
  // OAuth). Sin él, el callback de Google falla con "Index users.email not
  // found". Antes se llamaba `by_email`; se renombró (Convex no permite dos
  // índices con los mismos campos), y se actualizaron los usos internos.
  .index("email", ["email"])
  .index("by_company_id_and_email", ["internalControl.companyId", "email"])
  .index("by_company_id_and_role_id", ["internalControl.companyId", "roleId"])
  .index("by_is_super_admin", ["isSuperAdmin"])
  .index("by_role_id", ["roleId"])
  .index("by_branch_ids", ["branchIds"])
  .index("by_company_id", ["internalControl.companyId"])
  .index("by_branch_id", ["internalControl.branchId"])
  .index("by_state", ["internalControl.state"])
  .index("by_company_and_state", [
    "internalControl.companyId",
    "internalControl.state",
  ])
  .index("by_company_and_branch", [
    "internalControl.companyId",
    "internalControl.branchId",
  ])
  .index("by_branch_and_state", [
    "internalControl.branchId",
    "internalControl.state",
  ])
  .index("by_created_by", ["internalControl.createdBy"])
  .index("by_updated_by", ["internalControl.updatedBy"])
  .index("by_created_at", ["internalControl.createdAt"])
  .index("by_company_and_created_at", [
    "internalControl.companyId",
    "internalControl.createdAt",
  ]);
